// apps/admin/src/features/settings/StickySaveBar.tsx
//
// Phase 8.3 Wave 6 Task 41 — sticky Save / Discard bar shared by every
// System Settings tab (general / merchant / vendor — siblings 42/43 reuse
// the same component for order/notifications/security/support).
//
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-120
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per task-41 PLAN ("Sticky save bar 56px with 'Save Changes' + 'Discard' +
// dirty indicator") and-6.53.
//
// Behaviour contract:
//   - Renders nothing when `dirty=false` (no chrome stolen for a clean form).
//   - Sticks to the bottom of the page (position: sticky, bottom: 0) so it
//     follows long-scroll forms without overlap.
//   - "Save Changes" is disabled when `saving=true` and shows a spinner.
//   - "Discard" is disabled while saving (prevents losing in-flight PATCH).
//   - Permission gate: when `canWrite=false` the Save button is replaced by
//     a read-only badge so the form still surfaces field changes but never
//     triggers a 403 PATCH.
//
// Accessibility:
//   - role="region" + aria-label="Unsaved changes" so screen readers announce
//     the bar appearing.
//   - Save button has `aria-busy={saving}` while in flight.

import type { JSX } from 'react';

import { Button, cn } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

export interface StickySaveBarProps {
  /** Whether the form has un-persisted edits. Hides the bar when false. */
  dirty: boolean;
  /** PATCH in flight — disables both buttons + flips Save to a spinner. */
  saving: boolean;
  /** Caller holds the write permission for this namespace (false → read-only). */
  canWrite: boolean;
  /** Triggered by the Save button — caller PATCHes via the systemSettings API. */
  onSave: () => void;
  /** Triggered by the Discard button — caller resets local form state. */
  onDiscard: () => void;
  /** Optional override copy for the Save button label (default "Save Changes"). */
  saveLabel?: string;
  /** Optional override copy for the Discard button label (default "Discard"). */
  discardLabel?: string;
  /** Optional extra className appended to the root container. */
  className?: string;
}

/**
 * `<StickySaveBar>` — 56px sticky bottom bar. Single source of truth for the
 * Save / Discard affordance on every Settings tab so tabs share keyboard
 * focus order + button styling + dirty-indicator copy.
 */
export function StickySaveBar({
  dirty,
  saving,
  canWrite,
  onSave,
  onDiscard,
  saveLabel,
  discardLabel,
  className,
}: StickySaveBarProps): JSX.Element | null {
  const { t } = useTranslation('settings');

  if (!dirty) return null;

  return (
    <div
      role="region"
      aria-label={t('stickySaveBar.aria')}
      data-testid="settings-sticky-save-bar"
      className={cn(
        // 56px content area + 1px top border = 57px. Sticky to viewport
        // bottom so long forms still let the bar follow scroll.
        'sticky bottom-0 z-20 mt-6 flex h-14 items-center justify-between gap-3 border-t border-pw-border bg-pw-bg/95 px-4 backdrop-blur',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-sm text-pw-text-muted">
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full bg-pw-primary"
        />
        <span data-testid="settings-dirty-indicator">
          {t('stickySaveBar.unsavedChanges')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDiscard}
          disabled={saving}
          data-testid="settings-discard-btn"
        >
          {discardLabel ?? t('stickySaveBar.discard')}
        </Button>
        {canWrite ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onSave}
            disabled={saving}
            aria-busy={saving || undefined}
            data-testid="settings-save-btn"
          >
            {saving ? t('stickySaveBar.saving') : (saveLabel ?? t('stickySaveBar.save'))}
          </Button>
        ) : (
          <span
            data-testid="settings-readonly-badge"
            className="inline-flex h-9 items-center rounded-md border border-pw-border bg-pw-surface px-3 text-xs font-medium uppercase tracking-wide text-pw-text-muted"
          >
            {t('stickySaveBar.readonly')}
          </span>
        )}
      </div>
    </div>
  );
}

export default StickySaveBar;
